/* eslint-disable no-console */
import 'core-js/stable/object/values';
import 'core-js/stable/object/entries';
import 'core-js/stable/object/from-entries';
import * as React from 'react';
import { hydrate } from 'react-dom';
import Media from 'react-media';
import { ApolloProvider } from '@apollo/client';
import { Provider } from 'react-redux';
import type { History } from 'history';
import { Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { persistStore } from 'redux-persist';
import { blinkAPIClient } from './utils/blink-api-client';
import createGraphQLClient from './utils/graphql-client';
import shouldPersistStore from './utils/should-persist-store';
import createStore from './store/create-store';
import initializeStore from './store/initialize-store';
import { setDevice, Devices, updateHeader } from './store/modules/header-module';
import { setHydrated } from './store/modules/session-module';
import { updateMenu } from './store/modules/menu-module';
import { closeModal } from './store/modules/modal-module';
import smoothscroll from 'smoothscroll-polyfill';
import lastCampaign from 'last-campaign';
import ErrorBoundary from './components/error-boundary';
import Meta from './components/meta';
import { ensureReady, After, getSerializedData } from '@jaredpalmer/after';
import routes from './routes';
import 'styles/index.css';

// kick off the polyfill!
smoothscroll.polyfill();
lastCampaign({
  params: [
    // Default params:
    'utm_campaign',
    'utm_source',
    'utm_medium',
    'utm_term',
    'utm_content',
    'utm_type', // Additinal params we want to record:
    'utm_keyword',
    'utm_versionid',
    'utm_templateid',
    'utm_flowid',
    'utm_segmentid',
  ],
});
const history: History = createBrowserHistory<{ [key: string]: any }>();
const initialState = getSerializedData('state');
const store = createStore(initialState, {
  blinkAPIClient,
  history,
});
const graphQLClient = createGraphQLClient();
const mediaQueries = {
  mobile: '(min-width: 0px)',
  tablet: '(min-width: 768px)',
  desktop: '(min-width: 1200px)',
};

const mapProcessedMediaQueriesToDevice = (mediaQueries) =>
  mediaQueries.desktop ? Devices.desktop : mediaQueries.tablet ? Devices.tablet : Devices.mobile;

const init = () => {
  let hydrated = false;

  history.listen((location) => {
    store.dispatch(updateHeader(location.pathname, location.state));
    store.dispatch(updateMenu(location.pathname));
    store.dispatch(closeModal());
  });

  ensureReady(routes).then((data) => {
    hydrate(
      <Router history={history}>
        <ApolloProvider client={graphQLClient}>
          <Provider store={store}>
            <Media
              queries={mediaQueries}
              onChange={(processedMediaQueries) => {
                if (hydrated) {
                  store.dispatch(setDevice(mapProcessedMediaQueriesToDevice(processedMediaQueries)));
                }
              }}
            >
              <ErrorBoundary>
                <Meta />
                <After data={data} routes={routes} store={store} transitionBehavior="instant" />
              </ErrorBoundary>
            </Media>
          </Provider>
        </ApolloProvider>
      </Router>,
      document.getElementById('root'),
      () => {
        hydrated = true;
        initializeStore({ store, history });
        const processedMediaQueries = Object.keys(mediaQueries).reduce((result, queryName) => {
          const mediaMatch = window.matchMedia(mediaQueries[queryName]);
          result[queryName] = mediaMatch.matches;
          return result;
        }, {});
        store.dispatch(setDevice(mapProcessedMediaQueriesToDevice(processedMediaQueries)));
        store.dispatch(setHydrated(hydrated));
      }
    );
  });
  // Couldn't not figure any other way to trigger tearDown
  // without flow going crazy so opting for an event listener
  // that would be dispatched on logout from the account module
  document.addEventListener('TEARDOWN', tearDown);
};

let persistor;

if (shouldPersistStore()) {
  persistor = persistStore(
    store,
    null, // this is called when the state has been rehidrated
    init
  );
} else {
  init();
}

const tearDown = () => persistor.purge();

if (module.hot) {
  module.hot.accept();
}
